// src/components/TrainingAssignments.jsx

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/solid';

// Dummy data for demonstration
const DUMMY_EMPLOYEES = [
  { id: 'emp1', name: 'Alice Johnson' },
  { id: 'emp2', name: 'Bob Smith' },
  { id: 'emp3', name: 'Charlie Brown' },
];

const DUMMY_TRAININGS = [
  { id: 't1', title: 'Advanced React Hooks', description: 'Deep dive into custom hooks and state management.' },
  { id: 't2', title: 'Project Management Fundamentals', description: 'Learn the basics of Agile and Scrum methodologies.' },
  { id: 't3', title: 'Effective Communication', description: 'Strategies for improving workplace communication and conflict resolution.' },
];

const DUMMY_ASSIGNMENTS = [
  {
    id: 'a1',
    employeeId: 'emp1',
    employeeName: 'Alice Johnson',
    trainingTitle: 'Advanced React Hooks',
    dueDate: '2025-10-15',
    progress: 75,
  },
  {
    id: 'a2',
    employeeId: 'emp2',
    employeeName: 'Bob Smith',
    trainingTitle: 'Project Management Fundamentals',
    dueDate: '2025-10-30',
    progress: 25,
  },
];

const TrainingAssignments = () => {
  const [assignments, setAssignments] = useState(DUMMY_ASSIGNMENTS);
  const [formState, setFormState] = useState({
    employeeId: '',
    trainingId: '',
    dueDate: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formState.employeeId || !formState.trainingId || !formState.dueDate) {
      alert('Please fill out all fields.');
      return;
    }

    const newAssignment = {
      id: `a${assignments.length + 1}`,
      employeeId: formState.employeeId,
      employeeName: DUMMY_EMPLOYEES.find(emp => emp.id === formState.employeeId)?.name,
      trainingTitle: DUMMY_TRAININGS.find(train => train.id === formState.trainingId)?.title,
      dueDate: formState.dueDate,
      progress: 0,
    };

    setAssignments([newAssignment, ...assignments]);
    setFormState({
      employeeId: '',
      trainingId: '',
      dueDate: '',
    });
  };

  // Progress Bar Component
  const ProgressBar = ({ progress }) => (
    <div className="relative pt-1">
      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
        <div
          style={{ width: `${progress}%` }}
          className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
            progress < 100 ? 'bg-indigo-500' : 'bg-green-500'
          }`}
        ></div>
      </div>
      <span className="text-xs font-semibold inline-block text-indigo-600">
        {progress}% Complete
      </span>
      {progress === 100 && (
        <CheckCircleIcon className="h-4 w-4 text-green-500 ml-1 inline-block" />
      )}
    </div>
  );

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 flex items-center">
          <BookOpenIcon className="h-8 w-8 text-blue-500 mr-2" />
          Training Assignments
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Assign new training modules and track the progress of your team.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Assignment Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 h-fit"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <ChevronRightIcon className="h-6 w-6 text-orange-500 mr-2" />
            Assign New Training
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">
                Employee
              </label>
              <select
                id="employeeId"
                name="employeeId"
                value={formState.employeeId}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                required
              >
                <option value="">Select an employee</option>
                {DUMMY_EMPLOYEES.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="trainingId" className="block text-sm font-medium text-gray-700">
                Training Module
              </label>
              <select
                id="trainingId"
                name="trainingId"
                value={formState.trainingId}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                required
              >
                <option value="">Select a training</option>
                {DUMMY_TRAININGS.map((train) => (
                  <option key={train.id} value={train.id}>
                    {train.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                Due Date
              </label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formState.dueDate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                required
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Assign Training
            </motion.button>
          </form>
        </motion.div>

        {/* Assignments List */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-green-500 mr-2" />
            Current Assignments
          </h2>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            <AnimatePresence>
              {assignments.length > 0 ? (
                assignments.map((assignment) => (
                  <motion.div
                    key={assignment.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-sm text-gray-900">{assignment.employeeName}</div>
                      <span className="text-xs text-gray-500">Due: {assignment.dueDate}</span>
                    </div>
                    <p className="text-sm font-semibold text-indigo-600">{assignment.trainingTitle}</p>
                    <ProgressBar progress={assignment.progress} />
                  </motion.div>
                ))
              ) : (
                <div className="text-center text-gray-500 p-8">No training assignments found.</div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingAssignments;